import { storeToRefs } from "pinia";
import { ref, useNuxtApp, useRoute, useRouter, navigateTo } from "#imports";
import { Category } from "#sitis/internal/controllers/categories/models/Category";
import { catalogStore as useCatalogStore } from "#sitis/stores/modules/catalog-store";
import { productStore as useProductStore } from "#sitis/stores/modules/product-store";
export default function useSitisCategory(config) {
  const { $api } = useNuxtApp();
  const catalogStore = useCatalogStore();
  const productStore = useProductStore();
  const {
    filters,
    filterInfo,
    filterPages,
    filterPrices,
    currentCategory: category
  } = storeToRefs(catalogStore);
  const {
    products,
    pagination
  } = storeToRefs(productStore);
  const state = ref({
    extensions: config?.extensions || [],
    selectedFilter: {
      ...config?.selectedFilter || {}
    },
    sort: config?.defaultSort || "",
    basePathApi: config?.defaultBasePathApi || "/categories",
    page: config?.page || 1,
    perPage: config?.defaultPerPage || 20,
    saveMinPage: null,
    saveMaxPage: null,
    isLoadProducts: true,
    canceledOptions: config?.canceledOptions || {}
  });
  const router = useRouter();
  const route = useRoute();
  const categorySlug = config?.categorySlug || (((route?.fullPath || "").split("/f/")?.[0] || "").split("/").pop() || "").split("?")?.[0] || "";
  const getCategory = async () => {
    const searchParams = [];
    if (state.value.extensions && state.value.extensions.length) {
      searchParams.push(`expand=${state.value.extensions.join(",")}`);
    }
    const resCategory = await $api.agent.get(`${state.value.basePathApi}/${categorySlug}?${searchParams.join("&")}`).then((res) => res?._data).catch(() => null);
    if (!resCategory) {
      return;
    }
    const category2 = new Category(resCategory).toJSON();
    catalogStore.setCurrentCategory(category2);
    return category2;
  };
  const getProducts = async (settings) => {
    state.value.isLoadProducts = true;
    const params = {
      filters: _getSelectedFiltersForApi(),
      extensions: state.value.extensions,
      sort: state.value.sort,
      currentPage: state.value.page,
      perPage: state.value.perPage,
      slug: categorySlug,
      basePath: state.value.basePathApi,
      isLoadMore: Boolean(settings?.isLoadMore),
      isLoadPrevious: Boolean(settings?.isLoadPrevious)
    };
    const res = await productStore.getProducts(params);
    state.value.isLoadProducts = false;
    return res;
  };
  const onClearProductsList = () => {
    productStore.onClearCurrentProductsStore();
  };
  const getFilters = async () => {
    const params = {
      slug: categorySlug,
      slugs: [],
      filtersList: _getSelectedFiltersForApi(),
      basePath: state.value.basePathApi,
      canceledOptions: state.value?.canceledOptions || {}
    };
    await catalogStore.getFilters(params);
  };
  const _getArrayFilters = () => {
    const selectedFilters = { ...state.value.selectedFilter };
    const updateFiltersRequest = [];
    Object.keys(selectedFilters || {}).map((variantSlug) => {
      const values = [];
      Object.keys(selectedFilters[variantSlug]).map((value) => {
        values.push(value);
      });
      if (variantSlug === "categories") {
        const valueIds = _getCategoryIdsFromSlugs(values);
        valueIds.map((value) => {
          updateFiltersRequest.push({
            name: variantSlug,
            values: [value]
          });
        });
      } else {
        updateFiltersRequest.push({
          name: variantSlug,
          values
        });
      }
    });
    return updateFiltersRequest;
  };
  const _getSelectedFiltersForApi = (params) => {
    const values = [];
    const param = params || state.value.selectedFilter;
    const selectedFilter = { ...param };
    Object.keys(selectedFilter || {}).map((filterSlug) => {
      const filter = (filters.value || []).find((filter2) => filter2.slug === filterSlug);
      const filterTemplate = filter?.filterTemplate || "";
      const variants = selectedFilter?.[filterSlug] || {};
      Object.keys(variants).map((variantKey) => {
        const valueSlug = variants[variantKey];
        if (filterTemplate === "categories" || filterSlug === "categories") {
          values.push(`${filterSlug}[]=${_getCategoryIdFromSlug(valueSlug)}`);
        } else if (filterSlug === "text") {
          values.push(`${filterSlug}=${valueSlug}`);
        } else if (filterTemplate === "checkbox_list_with_range") {
          values.push(`filter[${filterSlug}]=${valueSlug}`);
        } else if (filterSlug === "priceFrom" || filterSlug === "priceTo") {
          values.push(`${filterSlug}=${valueSlug}`);
        } else {
          values.push(`filter[${filterSlug}][]=${valueSlug}`);
        }
      });
    });
    return values;
  };
  const getFilterInfo = async () => {
    if (Object.keys(state.value.selectedFilter || {}).length <= 0) {
      catalogStore.resetFilterInfo();
      return;
    }
    const path = process.client ? window.location?.pathname : route.fullPath;
    const response = await catalogStore.getFilterInfo({
      path
    });
    if (!response) {
      return;
    }
    if (response?.redirect) {
      return navigateTo(response?.redirect, {
        redirectCode: 301
      });
      return;
    }
  };
  const initSelectedFilter = () => {
    const { fullPath } = route;
    const filterString = ((fullPath || "").split("/f/")?.[1] || "").split("?")?.[0] || "";
    let selectedFilter = {
      ...config?.selectedFilter || {}
    };
    if (filterString) {
      filterString.split("/").map((filter) => {
        const [filterKey, filterValue] = (filter || "").split("=");
        const variants = (filterValue || "").split(",");
        variants.map((variant) => {
          selectedFilter = {
            ...selectedFilter,
            [filterKey]: {
              ...selectedFilter[filterKey] || {},
              [variant]: variant
            }
          };
        });
      });
    }
    const searchString = ((fullPath || "").split("/f/").pop() || "").split("?")?.[1] || "";
    if (searchString) {
      searchString.split("&").map((searchItem) => {
        const [searchKey, searchValue] = searchItem.split("=");
        state.value[searchKey] = searchValue;
      });
    }
    searchString.split("");
    state.value.selectedFilter = selectedFilter;
  };
  const changeSelectedFilter = async ({ key, value }) => {
    let selectedFilter = { ...state.value.selectedFilter };
    if (selectedFilter?.[key]?.[value]) {
      delete selectedFilter?.[key]?.[value];
      if (!Object.keys(selectedFilter?.[key]).length) {
        delete selectedFilter?.[key];
      }
    } else {
      selectedFilter = {
        ...selectedFilter,
        [key]: {
          ...selectedFilter[key],
          [value]: value
        }
      };
    }
    state.value.selectedFilter = selectedFilter;
    state.value.page = 1;
    state.value.saveMaxPage = null;
    state.value.saveMinPage = null;
    applyFilterToURL();
  };
  const changeSelectedFilterFull = async (selectedFilter) => {
    state.value.selectedFilter = selectedFilter;
    state.value.page = 1;
    state.value.saveMaxPage = null;
    state.value.saveMinPage = null;
    applyFilterToURL();
  };
  const loadMoreProducts = async () => {
    state.value.page = Number.parseFloat(String(state.value.saveMaxPage || state.value.page || "0")) + 1;
    state.value.saveMaxPage = Number.parseFloat(String(state.value.saveMaxPage || state.value.page || "0")) + 1;
    applyFilterToURL();
    await getProducts({ isLoadMore: true });
  };
  const loadPreviousProducts = async () => {
    state.value.page = Number.parseFloat(String(state.value.saveMinPage || state.value.page || "0")) - 1;
    state.value.saveMinPage = Number.parseFloat(String(state.value.saveMinPage || state.value.page || "0")) - 1;
    applyFilterToURL();
    await getProducts({ isLoadPrevious: true });
  };
  const changeCurrentPage = async (page) => {
    state.value.page = page;
    state.value.saveMaxPage = null;
    state.value.saveMinPage = null;
    applyFilterToURL();
    await getProducts();
  };
  const resetSelectedFilter = () => {
    state.value.selectedFilter = {};
    state.value.page = 1;
    state.value.saveMaxPage = null;
    state.value.saveMinPage = null;
    applyFilterToURL();
  };
  const changeSort = async (sort) => {
    state.value.sort = sort;
    applyFilterToURL();
    await getProducts();
  };
  const changePerPage = async (perPage) => {
    state.value.perPage = perPage;
    applyFilterToURL();
    await getProducts();
  };
  const initSaveMinMaxPage = () => {
    state.value.saveMinPage = Number.parseFloat(String(state.value.page || "1"));
    state.value.saveMaxPage = Number.parseFloat(String(state.value.page || "1"));
  };
  const applyFilterToURL = (isReturnOnlyFilter, filters2) => {
    if (config.isCancelApplyFilterToURL && !isReturnOnlyFilter) {
      return;
    }
    const newPageUrl = [];
    const selectedFilter = { ...filters2 || state.value.selectedFilter || {} };
    Object.keys(selectedFilter).map((filterKey) => {
      const variantsSlug = [];
      Object.keys(selectedFilter?.[filterKey] || {}).map((variantSlug) => {
        variantsSlug.push(variantSlug);
      });
      newPageUrl.push(`${filterKey}=${variantsSlug.join(",")}`);
    });
    const newPageSearch = [];
    if (state.value.page && state.value.page > 1) {
      newPageSearch.push(`page=${state.value.page}`);
    }
    if (state.value.sort) {
      newPageSearch.push(`sort=${state.value.sort}`);
    }
    let pagePath = "";
    if (!isReturnOnlyFilter) {
      pagePath = (route?.fullPath || "").split("/f/")?.[0].split("?")?.[0];
      if (pagePath.endsWith("/")) {
        pagePath = pagePath.slice(0, -1);
      }
    }
    const newFullPath = [
      pagePath,
      Boolean(newPageUrl.length) && `/f/${newPageUrl.join("/")}`,
      Boolean(newPageSearch.length) && `?${newPageSearch.join("&")}`
    ].filter((t) => !!t).join("");
    const sortedNewUrl = sortFilterString(newFullPath);
    if (isReturnOnlyFilter) {
      return sortedNewUrl;
    }
    if (process.client) {
      router.options.history.push(sortedNewUrl);
    }
  };
  const _getCategoryIdFromSlug = (categorySlug2) => {
    const filterCategories = catalogStore.categories || [];
    const findCategory = filterCategories.find((t) => t.slug === categorySlug2);
    if (!findCategory) {
      return categorySlug2;
    }
    return findCategory?.value || findCategory?.id || categorySlug2;
  };
  const _getCategoryIdsFromSlugs = (categorySlugs) => {
    const filterCategories = (filters.value || []).find((t) => t.slug === "categories");
    const variants = filterCategories?.variants || [];
    return categorySlugs.map((slug) => variants.find((t) => t.slug === slug)?.value || slug);
  };
  const sortFilterObject = (filter) => {
    let filterCharacteristics = {};
    let filterCategories = {};
    let filterPrices2 = {};
    const functionSorted = function(object) {
      return Object.keys(object).sort().reduce(
        (obj, key) => {
          obj[key] = object[key];
          return obj;
        },
        {}
      );
    };
    Object.keys(filter).map((key) => {
      if (key === "categories") {
        filterCategories[key] = filter[key];
      } else if (key === "priceFrom" || key === "priceTo") {
        filterPrices2[key] = filter[key];
      } else {
        filterCharacteristics[key] = filter[key];
      }
    });
    Object.keys(filterCharacteristics).map((key) => {
      filterCharacteristics[key] = functionSorted(filterCharacteristics[key]);
    });
    Object.keys(filterCategories).map((key) => {
      filterCategories[key] = functionSorted(filterCategories[key]);
    });
    Object.keys(filterPrices2).map((key) => {
      filterPrices2[key] = functionSorted(filterPrices2[key]);
    });
    filterCharacteristics = functionSorted(filterCharacteristics);
    filterCategories = functionSorted(filterCategories);
    filterPrices2 = functionSorted(filterPrices2);
    return {
      ...filterCharacteristics,
      ...filterCategories,
      ...filterPrices2
    };
  };
  const sortFilterString = (fullUrl) => {
    const beforeFilter = (fullUrl || "").split("/f/")?.[0];
    const afterFilter = ((fullUrl || "").split("/f/")?.[1] || "").split("?")?.[1] || "";
    const filterString = ((fullUrl || "").split("/f/")?.[1] || "").split("?")?.[0] || "";
    let selectedFilters = {};
    if (filterString) {
      filterString.split("/").forEach((filter) => {
        const [s, variants] = filter.split("=");
        selectedFilters[s] = {};
        (variants || "").split(",").forEach((variant) => {
          const v = variant.trim();
          selectedFilters[s][v] = v;
        });
      });
    }
    selectedFilters = sortFilterObject(selectedFilters);
    let updateFiltersRequest = [];
    Object.keys(selectedFilters || {}).map((variantSlug) => {
      const values = [];
      Object.keys(selectedFilters[variantSlug]).map((value) => {
        values.push(value);
      });
      updateFiltersRequest.push(`${variantSlug}=${values.join(",")}`);
    });
    updateFiltersRequest = updateFiltersRequest.sort((a, b) => {
      const aSlug = a.split("=")?.[0] || "";
      const bSlug = b.split("=")?.[0] || "";
      const aSort = (filters.value || []).find((t) => t.slug === aSlug)?.urlSort || 1;
      const bSort = (filters.value || []).find((t) => t.slug === bSlug)?.urlSort || 1;
      if (aSort < bSort) {
        return 1;
      }
      if (aSort > bSort) {
        return -1;
      }
      return 0;
    });
    updateFiltersRequest = updateFiltersRequest.join("/");
    let newUrl = beforeFilter;
    if (updateFiltersRequest) {
      newUrl = `${newUrl}/f/${updateFiltersRequest}`;
    }
    if (afterFilter) {
      newUrl = `${newUrl}?${afterFilter}`;
    }
    return newUrl;
  };
  return {
    state,
    category,
    products,
    pagination,
    filters,
    filterInfo,
    filterPages,
    filterPrices,
    initSelectedFilter,
    applyFilterToURL,
    getCategory,
    getProducts,
    onClearProductsList,
    getFilters,
    getFilterInfo,
    changeCurrentPage,
    changeSelectedFilter,
    changeSelectedFilterFull,
    changeSort,
    changePerPage,
    loadPreviousProducts,
    loadMoreProducts,
    resetSelectedFilter,
    initSaveMinMaxPage,
    _getSelectedFiltersForApi
  };
}
